import { Filters, SearchMetadata } from "@nerdjs/nerd-core";
import {
  DeleteAction,
  GetAction,
  PostAction,
  PutAction,
} from "@nerdjs/nerd-network";
import { AnyAction, createAction, Dispatch, nanoid } from "@reduxjs/toolkit";
import { User, User_Entity } from "../../entities/user";

export const setStagedUser = createAction(
  "setStagedUser",
  function prepare(stagedUser?: Partial<User_Entity>) {
    return {
      payload: {
        stagedUser,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setUserFormOpen = createAction(
  "setUserFormOpen",
  function prepare(open: boolean) {
    return {
      payload: {
        open,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setUserLoading = createAction(
  "setUserLoading",
  function prepare(loading: boolean) {
    return {
      payload: {
        loading,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setUserSearchOpen = createAction(
  "setUserSearchOpen",
  function prepare(searchOpen: boolean) {
    return {
      payload: {
        searchOpen,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setUserFilters = createAction(
  "setUserFilters",
  function prepare(filters?: Filters) {
    return {
      payload: {
        filters,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateStagedUser = createAction(
  "updateStagedUser",
  function prepare(stagedUser?: Partial<User_Entity>) {
    return {
      payload: {
        stagedUser,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setSelectedUserID = createAction(
  "setSelectedUserID",
  function prepare(userID?: number) {
    return {
      payload: {
        userID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getUsers = createAction(
  "kronos/user/getUsers",
  function prepare() {
    const request = new GetAction(`/kronos/users`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const users = User.fromList(json);
      dispatch(getUsersSuccess(users));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getUsersSuccess = createAction(
  "kronos/user/getUserSuccess",
  function prepare(users: User[]) {
    return {
      payload: {
        users,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getUsersByFilter = createAction(
  "kronos/user/getUsersByFilter",
  function prepare(query: { [key: string]: unknown }) {
    const request = new GetAction(`/kronos/users`, query);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const users = User.fromList(json);
      dispatch(getUsersByFilterSuccess(users));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getUsersByFilterSuccess = createAction(
  "kronos/user/getUsersByFilterSuccess",
  function prepare(users: User[]) {
    return {
      payload: {
        users,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getUserById = createAction(
  "kronos/user/getById",
  function prepare(id: number) {
    const request = new GetAction(`/kronos/users/${id}`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const user = new User(json);
      dispatch(getUserByIdSuccess(user));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getUserByIdSuccess = createAction(
  "kronos/user/getUserByIdSuccess",
  function prepare(user?: User) {
    return {
      payload: {
        user,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const deleteUser = createAction(
  "kronos/removeUserById",
  function prepare(id: number, successAction?: AnyAction) {
    const request = new DeleteAction(`/kronos/users/${id}`);

    if (successAction)
      request.decodeResponse = (dispatch: Dispatch) => {
        dispatch(deleteUserSuccess(id));
      };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const deleteUserSuccess = createAction(
  "kronos/users/deleteUserSuccess",
  function prepare(userID: number) {
    return {
      payload: {
        userID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateUser = createAction(
  "kronos/users/updateUser",
  function prepare(id: number, body: Partial<User>) {
    const request = new PutAction(`/kronos/users/${id}`, body);

    request.decodeResponse = (dispatch: Dispatch) => {
      dispatch(updateUserSuccess(true));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const updateUserSuccess = createAction(
  "kronos/users/updateUserSuccess",
  function prepare(success: boolean) {
    return {
      payload: {
        success,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const createUser = createAction(
  "kronos/users/createUser",
  function prepare(body: Partial<User>) {
    const request = new PostAction(`/kronos/users`, body);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const user = new User(json);
      dispatch(createUserSuccess(user));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const createUserSuccess = createAction(
  "kronos/users/createUserSuccess",
  function prepare(user: User) {
    return {
      payload: {
        user,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getUsersSearchMetadata = createAction(
  "kronos/getUsersSearchMetadata",
  function prepare() {
    const request = new GetAction(`/kronos/users/searchMetadata`);

    request.decodeResponse = (dispatch: Dispatch, json: SearchMetadata) => {
      dispatch(getUsersSearchMetadataSuccess(json));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getUsersSearchMetadataSuccess = createAction(
  "kronos/getUsersSearchMetadataSuccess",
  function prepare(usersSearchMetadata: SearchMetadata) {
    return {
      payload: {
        usersSearchMetadata,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);
